import PropTypes from 'prop-types';
import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import Global from '../../../store/global-selectors';
import { applyCarrierSharingDefaults } from './api';
import { useCarrierSharing, useProviders } from './hooks';
import {
  extractActiveNotIntegratedExternalClientConnectionIdFromProviders,
  extractClientExternalConnectionId,
  getFfProviders,
  getVendors,
} from './utils';

const Context = createContext();

export const useProvidersGridContext = () => {
  return useContext(Context);
};

const isCounterDefault = (counter) => counter?.isDefault;

const getAllCounters = (gridDataList) => extractActiveNotIntegratedExternalClientConnectionIdFromProviders(gridDataList);

const getDefaultCounters = (gridDataList) => gridDataList.filter(isCounterDefault).map(extractClientExternalConnectionId);

const getGetCountersFn = (applyToAll) => (applyToAll ? getAllCounters : getDefaultCounters);

export const ProvidersGridContext = ({ children }) => {
  const carriers = useSelector(Global.selectReferenceDatagetProviders);
  const labelProviders = useSelector(Global.selectSellerLabelProviders);
  const activeServices = useSelector(Global.selectSellerActiveServices);

  const carrierSharing = useCarrierSharing();

  const providersHandler = useProviders();

  const vendors = getVendors(providersHandler);

  const fulfillmentProviders = getFfProviders(providersHandler);

  const getCounterIdsByProvider = (getAll) => {
    const getCounters = getGetCountersFn(getAll);
    return {
      vendorCounterIds: getCounters(vendors.list),
      ffProviderCounterIds: getCounters(fulfillmentProviders.list),
    };
  };

  const updateAllCounters = (counterIdsByProvider) => {
    vendors.updateDataGridProviders(counterIdsByProvider.vendorCounterIds);
    fulfillmentProviders.updateDataGridProviders(counterIdsByProvider.ffProviderCounterIds);
  };

  const updateCountersDefaults = (applyToAll) => {
    const counterIdsByProvider = getCounterIdsByProvider(applyToAll);
    if (applyToAll) {
      const clientExternalConnectionIds = [...counterIdsByProvider.vendorCounterIds, ...counterIdsByProvider.ffProviderCounterIds];
      applyCarrierSharingDefaults({ clientExternalConnectionIds }).then(() => {
        updateAllCounters(counterIdsByProvider);
      });
    } else {
      updateAllCounters(counterIdsByProvider);
    }
  };

  const calculateCounters = () => {
    const counterIdsByProvider = getCounterIdsByProvider(true);
    return counterIdsByProvider.vendorCounterIds.length + counterIdsByProvider.ffProviderCounterIds.length;
  };

  const value = {
    carriers,

    vendors,
    fulfillmentProviders,
    carrierSharing,

    labelProviders,
    activeServices,

    updateCountersDefaults,

    calculateCounters,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

ProvidersGridContext.propTypes = {
  children: PropTypes.node.isRequired,
};
